/**
 * Linking a signed-in identity to a wallet — `POST /v1/registrations`.
 *
 * # Why this is a separate act from signing up
 *
 * Ory creates an *identity*. It does not create a *customer*, and the backend
 * is emphatic that it must not: `registration.go` rejects auto-provisioning
 * because it "would create customers as a side effect of any authenticated
 * request, and it would do so with no country — which invariant 7 requires and
 * which only the caller can supply. Onboarding is a decision, not a lazy
 * initialisation."
 *
 * That decision is taken here, on this screen, by the person it concerns. Until
 * it is, every customer route answers `403 not_a_customer` — which is exactly
 * what a new customer met before this module existed (`go-wallet-web#22`).
 *
 * # The refusals are four different things and must stay four different things
 *
 * `go-wallet#308` was filed because the backend collapsed a registry outage
 * into "your country is closed": *"A client treats 403 as permanent. The
 * customer is told, falsely and durably, that we do not serve their country,
 * and they stop trying."* The backend now separates them. A client that renders
 * one sentence for both re-creates the bug on this side of the wire, so the
 * taxonomy below is deliberately not collapsible into a string.
 */

import { ApiError } from '@/lib/api'
import type { WalletApi } from '@/lib/api'
import type { ConsentText } from '@/privacy/consentTexts'

/** `storage.SubjectKind`. The contract's own default is `individual`. */
export type SubjectKind = 'individual' | 'company'

/**
 * One acceptance, built from the text that was ON THE SCREEN (`#117`).
 *
 * Takes the whole `ConsentText` rather than a purpose string so the version and
 * hash cannot come from anywhere except the item that was rendered. Passing
 * them separately would make it possible — and eventually inevitable — to pair
 * a purpose with a hash from a different read, which is the exact failure the
 * hashed-wording design exists to prevent.
 */
export interface ConsentAcceptance {
  readonly text: ConsentText
  /**
   * The affirmative act (Art. 7(2)), from the control that was used. Never
   * defaulted: "they agreed" is not a record; "they ticked an unticked box" is.
   */
  readonly action: 'checkbox_ticked' | 'button_pressed' | 'form_submitted'
}

/** This is the web app, so the channel is `web` and nothing else. */
export const CONSENT_CHANNEL = 'web' as const

export interface RegistrationInput {
  /**
   * ISO-3166 alpha-2. **Never defaulted, anywhere in this path** — not from the
   * locale, not from the browser, not from a previous answer. `registration.go`
   * refuses an empty country rather than guessing, and a client that guesses on
   * its behalf onboards someone outside the mandate with no record of who chose.
   */
  readonly country: string
  readonly legalName: string
  readonly email: string
  readonly kind: SubjectKind
  /**
   * Where this person lives.
   *
   * REQUIRED, and required *here* rather than later, because the institution
   * that issues the account requires it: an account is titled to a person and
   * they ask where that person lives. A registration that omits it is refused
   * by `POST /v1/registrations` (`go-wallet#1039`).
   *
   * Asking at sign-up is not a preference. Acceptance is the institution's
   * verdict, not a screen — somebody accepted without an address is told they
   * have been accepted and then cannot be issued an IBAN, and that refusal
   * arrives hours later with nobody left to ask.
   */
  readonly addressLine1: string
  readonly addressLine2: string
  readonly addressCity: string
  /**
   * OPTIONAL, and deliberately not required. Several countries do not use one,
   * and demanding it would refuse addresses the institution accepts.
   */
  readonly addressPostalCode: string
  /**
   * What this person was shown and accepted, from the read that was rendered.
   *
   * REQUIRED rather than optional, so a caller has to say which it means:
   * "nothing was published" and "we could not read the wording" are the same
   * empty array and opposite facts. `mayRegister` refuses to submit in the
   * second, because a customer whose file records that nothing was asked of
   * them, created on a day we could not tell whether anything was, is a false
   * statement in a table that cannot be edited.
   */
  readonly accepted: readonly ConsentAcceptance[]
}

/** Which input a field-level refusal is about, when it is about one. */
export type RegistrationField =
  | 'country'
  | 'legalName'
  | 'email'
  | 'kind'
  | 'addressLine1'
  | 'addressCity'

/**
 * Why a registration was refused, in the terms that decide what to *offer*.
 *
 * The discriminant is the remedy, not the status code, because the remedy is
 * what the screen has to get right: `retry` shows a retry button, `closed` does
 * not, `support` offers neither because there is nothing the customer can
 * change about the request that would make it succeed (`go-wallet#302`).
 */
export type RegistrationRefusal =
  /** A field is missing or malformed. Fixable here, by this person, now. */
  | { readonly kind: 'field'; readonly field?: RegistrationField; readonly message: string }
  /** Invariant 7: this country is not open. Permanent until an operator opens it. */
  | { readonly kind: 'closed'; readonly message: string; readonly decisionRef?: string }
  /** We could not find out. Transient — and retrying is safe. */
  | { readonly kind: 'retry'; readonly message: string }
  /** A live wallet already holds this email, and it is not this identity. */
  | { readonly kind: 'email_taken'; readonly message: string }
  /** The identity belongs to an offboarded wallet. Only support can move this. */
  | { readonly kind: 'support'; readonly message: string }
  /** The session went away underneath us. */
  | { readonly kind: 'signin'; readonly message: string }
  /**
   * The wording moved, or was not quoted, and NO customer was created.
   *
   * Its own kind rather than `field` or `retry`, because the remedy is neither:
   * the screen must RE-READ the wording, show the new text, and ask again.
   * Retrying the same body would answer a version the person never saw, and a
   * field error would point them at an input they cannot fix.
   */
  | { readonly kind: 'consent'; readonly message: string; readonly reread: boolean }
  | { readonly kind: 'unknown'; readonly message: string; readonly decisionRef?: string }

export type RegistrationOutcome =
  | {
      readonly status: 'registered'
      /**
       * `false` when this identity was already registered.
       *
       * Success either way — the endpoint is idempotent on the Ory subject, so
       * a double submit, a browser retry or a resumed signup is one
       * registration. Treating `created: false` as an error would show a
       * failure for an operation that completed.
       */
      readonly created: boolean
    }
  | { readonly status: 'refused'; readonly refusal: RegistrationRefusal }

const FIELD_OF: Record<string, RegistrationField> = {
  country_required: 'country',
  legal_name_required: 'legalName',
  email_required: 'email',
  kind_invalid: 'kind',
  address_line1_required: 'addressLine1',
  address_city_required: 'addressCity',
}

/**
 * Turn a failure into the refusal it actually is.
 *
 * Exported for its own test: this mapping is the whole point of the module and
 * a regression in it is invisible on screen until someone is in the country
 * that is briefly unreachable.
 */
export function classifyRegistrationRefusal(err: unknown): RegistrationRefusal {
  if (!(err instanceof ApiError)) {
    // A transport failure. Retryable, and retrying is *safe* rather than merely
    // permitted: the endpoint is idempotent on the Ory subject, so a request
    // that succeeded before the connection dropped returns the same customer.
    return {
      kind: 'retry',
      message: 'We could not reach Tesora. Please check your connection and try again.',
    }
  }

  const withRef = err.decisionRef === undefined ? {} : { decisionRef: err.decisionRef }

  switch (err.code) {
    case 'country_required':
    case 'legal_name_required':
    case 'email_required':
    case 'kind_invalid': {
      const field = FIELD_OF[err.code]
      return { kind: 'field', ...(field === undefined ? {} : { field }), message: err.message }
    }
    case 'malformed':
      return { kind: 'field', message: err.message }
    case 'jurisdiction_closed':
      return { kind: 'closed', message: err.message, ...withRef }
    case 'temporarily_unavailable':
      return { kind: 'retry', message: err.message }
    case 'email_taken':
      return { kind: 'email_taken', message: err.message }
    case 'identity_already_linked':
      return { kind: 'support', message: err.message }
    case 'unauthenticated':
      return { kind: 'signin', message: err.message }

    // The four the backend publishes for a registration whose acceptances do
    // not hold up. `consent_text_changed` is the one that MUST re-read; the
    // other three mean this client sent an incomplete acceptance, which is a
    // defect here rather than something the person did — so they surface rather
    // than being swallowed, and re-reading is harmless.
    case 'consent_text_changed':
    case 'consent_required':
    case 'consent_action_required':
    case 'consent_channel_required':
      return { kind: 'consent', message: err.message, reread: true }
  }

  // Fall back on the STATUS, so a code this bundle predates is still routed to
  // the right remedy rather than to a dead end. A browser bundle can be months
  // older than the backend, and 5xx must stay retryable even when its code is
  // new vocabulary.
  if (err.status >= 500 || err.status === 429) {
    return { kind: 'retry', message: err.message }
  }
  if (err.status === 401) return { kind: 'signin', message: err.message }
  return { kind: 'unknown', message: err.message, ...withRef }
}

interface RegistrationDTO {
  created?: unknown
}

/**
 * Register (`POST /v1/registrations`).
 *
 * Refusals are *returned*, not thrown: every one of them is an expected outcome
 * with a screen behind it, and expected outcomes routed through `catch` are the
 * ones that eventually get caught by something generic and rendered as "an
 * error occurred".
 *
 * # What this deliberately does not carry back
 *
 * `customer_id` — an internal identifier with no use in a browser, and a JSON
 * `int64` besides, which is the shape `CLAUDE.md` §3 exists to be careful about.
 *
 * `state` — because `GET /v1/onboarding` is the source of truth for where a
 * customer stands, and seeding it from a write response would create a second
 * one that disagrees the moment anything else moves the state machine. The
 * caller re-reads instead.
 */
export async function register(
  api: WalletApi,
  idempotencyKey: string,
  input: RegistrationInput,
): Promise<RegistrationOutcome> {
  try {
    const dto = await api.post<RegistrationDTO>(
      '/v1/registrations',
      {
        country: input.country,
        legal_name: input.legalName,
        email: input.email,
        kind: input.kind,
        address_line1: input.addressLine1,
        address_city: input.addressCity,
        // SENT EVEN WHEN EMPTY, unlike `accepted` below. These are optional
        // fields with an ordinary blank value, not a claim about what was
        // asked — the backend stores NULL for a blank and the two are the same
        // fact here.
        address_line2: input.addressLine2,
        address_postal_code: input.addressPostalCode,
        // OMITTED ENTIRELY when there is nothing to say, rather than sent as
        // `[]`. A deployment with no published wording asks for nothing; an
        // empty array is a positive claim that nothing was accepted, and the
        // two are not the same sentence in a record read later by a supervisory
        // authority.
        ...(input.accepted.length === 0
          ? {}
          : {
              accepted: input.accepted.map((a) => ({
                purpose: a.text.purpose,
                version: a.text.version,
                text_sha256: a.text.textSha256,
                action: a.action,
                channel: CONSENT_CHANNEL,
              })),
            }),
      },
      { idempotencyKey },
    )
    return { status: 'registered', created: dto?.created === true }
  } catch (err) {
    return { status: 'refused', refusal: classifyRegistrationRefusal(err) }
  }
}
